import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Users, Search, UserCheck, UserX, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (search) params.search = search;
      
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isActive ? 'deactivate' : 'activate';
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/create-admin`,
        { ...adminForm, role: 'admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Admin account created successfully!');
      setShowCreateAdmin(false);
      setAdminForm({ email: '', password: '', full_name: '', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setCreatingAdmin(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <UserPlus className="w-6 h-6 text-indigo-600" />
                Create Admin Account
              </h2>
              <button
                onClick={() => setShowCreateAdmin(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={creatingAdmin}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <Label htmlFor="admin-name">Full Name *</Label>
                <Input
                  id="admin-name"
                  value={adminForm.full_name}
                  onChange={(e) => setAdminForm({...adminForm, full_name: e.target.value})}
                  required
                  disabled={creatingAdmin}
                />
              </div>

              <div>
                <Label htmlFor="admin-email">Email *</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                  required
                  disabled={creatingAdmin}
                />
              </div>

              <div>
                <Label htmlFor="admin-phone">Phone (optional)</Label>
                <Input
                  id="admin-phone"
                  type="tel"
                  value={adminForm.phone}
                  onChange={(e) => setAdminForm({...adminForm, phone: e.target.value})}
                  disabled={creatingAdmin}
                />
              </div>

              <div>
                <Label htmlFor="admin-password">Password *</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                  required
                  minLength={8}
                  disabled={creatingAdmin}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAdmin(false)}
                  disabled={creatingAdmin}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingAdmin}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {creatingAdmin ? 'Creating...' : 'Create Admin'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">User Management</h2>
        </div>
        <Button
          onClick={() => setShowCreateAdmin(true)}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Create Admin
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border rounded-md"
          >
            <option value="all">All Roles</option>
            <option value="candidate">Candidates</option>
            <option value="recruiter">Recruiters</option>
            <option value="admin">Admins</option>
          </select>
          <Button type="submit">Search</Button>
        </form>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12">Loading users...</div>
      ) : (
        <div className="grid gap-4">
          {users.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              No users found
            </Card>
          ) : (
            users.map((user) => (
              <Card key={user.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{user.full_name}</h3>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                      {user.is_verified && (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {user.is_active === false && (
                        <Badge variant="destructive">
                          Deactivated
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span>KYC: {user.kyc_status || 'not_submitted'}</span>
                      {user.trust_score && (
                        <span>Trust Score: {user.trust_score.toFixed(1)}/100</span>
                      )}
                      <span>Joined: {new Date(user.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={user.is_active === false ? 'default' : 'destructive'}
                      onClick={() => toggleUserStatus(user.id, user.is_active !== false)}
                    >
                      {user.is_active === false ? (
                        <><UserCheck className="w-4 h-4 mr-1" /> Activate</>
                      ) : (
                        <><UserX className="w-4 h-4 mr-1" /> Deactivate</>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
